import { useSeo } from "@/hooks/useSeo";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";

const PrivacyPage = () => {
  useSeo('Privacy Notice | Luxe Lords', 'How Luxe Lords collects, uses and protects your personal data.', '/privacy');
  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      <main className="pt-32 pb-20 px-6 lg:px-12">
        <article className="container mx-auto max-w-3xl prose-content">
          
          <h1 className="font-serif text-3xl md:text-4xl font-normal tracking-tight text-foreground mb-3">Privacy Notice</h1>
          <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-14">Last updated: 19 May 2026</p>

          <div className="space-y-8 text-body text-muted-foreground leading-relaxed">
            <section>
              <h2 className="font-serif text-xl md:text-2xl font-normal tracking-tight text-foreground mb-3">1. Who we are</h2>
              <p>
                Luxe Lords is a brand operated by <strong>Easy Box Ltd</strong> ("we", "us",
                "our"), a company registered in England and Wales. For the purposes of the UK
                GDPR and the Data Protection Act 2018, Easy Box Ltd is the data controller for
                personal data collected through this website.
              </p>
              <p className="mt-2">
                <strong>Contact:</strong> info@luxelords.com
              </p>
              <p className="mt-2 text-sm">
                Easy Box Ltd is exempt from paying the ICO data protection fee under the Data
                Protection (Charges and Information) Regulations 2018, as we only process
                personal data for core business purposes (orders, customer service and
                record-keeping) and do not carry out electronic marketing, profiling or CCTV
                monitoring.
              </p>
            </section>

            <section>
              <h2 className="font-serif text-xl md:text-2xl font-normal tracking-tight text-foreground mb-3">2. What data we collect</h2>
              <ul className="list-disc pl-5 space-y-1">
                <li><strong>Order data</strong> — name, delivery and billing address, email, phone, items ordered (processed by our checkout provider, Shopify).</li>
                <li><strong>Contact form data</strong> — name, email, subject, optional order number, message and consent record.</li>
                <li><strong>Support chat data</strong> — the messages you type in our on-site chat widget.</li>
                <li><strong>Technical data</strong> — IP address, browser type, device info and pages visited (used only for security and to operate the site).</li>
              </ul>
            </section>

            <section>
              <h2 className="font-serif text-xl md:text-2xl font-normal tracking-tight text-foreground mb-3">3. Why we use it &amp; legal basis</h2>
              <ul className="list-disc pl-5 space-y-1">
                <li><strong>To fulfil orders</strong> — contractual necessity (UK GDPR Art. 6(1)(b)).</li>
                <li><strong>To respond to enquiries</strong> — your consent given via the contact form (Art. 6(1)(a)).</li>
                <li><strong>To operate the support chat</strong> — legitimate interest in providing customer service (Art. 6(1)(f)).</li>
                <li><strong>To keep the site secure</strong> — legitimate interest in preventing fraud and abuse (Art. 6(1)(f)).</li>
                <li><strong>To comply with legal duties</strong> — tax, accounting and consumer law (Art. 6(1)(c)).</li>
              </ul>
            </section>

            <section>
              <h2 className="font-serif text-xl md:text-2xl font-normal tracking-tight text-foreground mb-3">4. Who we share data with</h2>
              <p>We only share personal data with processors that help us run the shop:</p>
              <ul className="list-disc pl-5 space-y-1 mt-2">
                <li><strong>Shopify Inc.</strong> — checkout, payment processing and order management.</li>
                <li><strong>Supabase</strong> — secure storage of contact form messages and chat sessions (EU region).</li>
                <li><strong>Google / OpenAI (via Lovable AI Gateway)</strong> — powers the support chatbot. Messages are sent for the sole purpose of generating a reply and are not used to train models.</li>
                <li><strong>Couriers</strong> — to deliver your order.</li>
              </ul>
              <p className="mt-2">We do not sell your personal data and we do not share it with advertisers.</p>
            </section>

            <section>
              <h2 className="font-serif text-xl md:text-2xl font-normal tracking-tight text-foreground mb-3">5. International transfers</h2>
              <p>
                Some processors (Shopify, OpenAI) are based outside the UK/EEA. Where data is
                transferred, we rely on the UK International Data Transfer Addendum and EU
                Standard Contractual Clauses to ensure an equivalent level of protection.
              </p>
            </section>

            <section>
              <h2 className="font-serif text-xl md:text-2xl font-normal tracking-tight text-foreground mb-3">6. How long we keep it</h2>
              <ul className="list-disc pl-5 space-y-1">
                <li>Order records: 7 years (UK tax law).</li>
                <li>Contact form messages: up to 24 months from last contact.</li>
                <li>Chat transcripts: up to 90 days for service-quality review.</li>
              </ul>
            </section>

            <section>
              <h2 className="font-serif text-xl md:text-2xl font-normal tracking-tight text-foreground mb-3">7. Your rights</h2>
              <p>Under UK GDPR you have the right to:</p>
              <ul className="list-disc pl-5 space-y-1 mt-2">
                <li>Access a copy of your personal data.</li>
                <li>Have inaccurate data corrected.</li>
                <li>Request erasure ("right to be forgotten").</li>
                <li>Restrict or object to processing.</li>
                <li>Data portability.</li>
                <li>Withdraw consent at any time.</li>
              </ul>
              <p className="mt-2">
                Email <a className="underline hover:text-foreground" href="mailto:info@luxelords.com">info@luxelords.com</a> to exercise any of these rights. We aim to respond within one month.
              </p>
              <p className="mt-2">
                You can also complain to the UK Information Commissioner's Office at{" "}
                <a className="underline hover:text-foreground" href="https://ico.org.uk" target="_blank" rel="noopener noreferrer">ico.org.uk</a>.
              </p>
            </section>

            <section>
              <h2 className="font-serif text-xl md:text-2xl font-normal tracking-tight text-foreground mb-3">8. Cookies</h2>
              <p>
                See our <a className="underline hover:text-foreground" href="/cookies">Cookie Policy</a> for details of the cookies and local storage we use.
              </p>
            </section>

            <section>
              <h2 className="font-serif text-xl md:text-2xl font-normal tracking-tight text-foreground mb-3">9. Changes</h2>
              <p>
                We may update this notice from time to time. Material changes will be highlighted
                on this page with a revised "last updated" date.
              </p>
            </section>
          </div>
        </article>
      </main>
      <Footer />
    </div>
  );
};

export default PrivacyPage;
